/**
 * PlansAdminPage - Admin interface for managing billing plans.
 * 
 * Features:
 * - CRUD operations with optimistic updates
 * - Stripe sync status monitoring
 * - Audit trail viewer
 * - Rate limiting indicators
 * 
 * @accessibility WCAG 2.1 AA compliant (focus management, ARIA)
 * @i18n Ready for translation (no hardcoded text)
 */
import React, { useState, useCallback, useMemo } from 'react'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  Check,
  X,
  Star,
  Zap,
  AlertTriangle,
  History,
  CreditCard,
  ChevronDown,
  ExternalLink,
} from 'lucide-react'
import { useAdminPlans, useStripeStatus, AdminPlan, ALLOWED_FEATURES } from '../hooks/useAdminPlans'
import PlanEditorModal from '../components/billing/PlanEditorModal'

// =============================================================================
// Formatters
// =============================================================================

const currencyFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

// =============================================================================
// Components
// =============================================================================

function StatusBadge({ active, hidden }: { active: boolean; hidden: boolean }) {
  if (!active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-700 text-zinc-300">
        <X className="w-3 h-3" />
        Inactif
      </span>
    )
  }
  if (hidden) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/50 text-amber-300">
        <EyeOff className="w-3 h-3" />
        Masqué
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/50 text-emerald-300">
      <Check className="w-3 h-3" />
      Actif
    </span>
  )
}

function StripeBadge({ synced }: { synced: boolean }) {
  if (synced) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-900/50 text-indigo-300">
        <CreditCard className="w-3 h-3" />
        Stripe
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-800 text-zinc-500">
      <CreditCard className="w-3 h-3" />
      Non sync
    </span>
  )
}

function CircuitStatusBadge({ state }: { state: string }) {
  const colors = {
    closed: 'bg-emerald-900/50 text-emerald-300',
    open: 'bg-red-900/50 text-red-300',
    half_open: 'bg-amber-900/50 text-amber-300',
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[state as keyof typeof colors] || colors.closed}`}>
      <Zap className="w-3 h-3" />
      Circuit: {state}
    </span>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function PlansAdminPage() {
  // Data hooks
  const { plans, loading, error, refetch, toggleActive, syncStripe, deletePlan, getAuditLog } = useAdminPlans()
  const { circuitStatus, dlqEntries } = useStripeStatus()

  // UI State
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [showEditor, setShowEditor] = useState(false)
  const [editingPlan, setEditingPlan] = useState<AdminPlan | null>(null)
  const [showAudit, setShowAudit] = useState<number | null>(null)
  const [auditLog, setAuditLog] = useState<any[]>([])
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // Filtered plans
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        if (!plan.name.toLowerCase().includes(search) && 
            !plan.slug.toLowerCase().includes(search)) {
          return false
        }
      }
      // Status filter
      if (filterStatus === 'active' && !plan.is_active) return false
      if (filterStatus === 'inactive' && plan.is_active) return false
      
      return true
    }).sort((a, b) => a.sort_order - b.sort_order)
  }, [plans, searchTerm, filterStatus])

  // Handlers
  const handleCreate = useCallback(() => {
    setEditingPlan(null)
    setShowEditor(true)
  }, [])

  const handleEdit = useCallback((plan: AdminPlan) => {
    setEditingPlan(plan)
    setShowEditor(true)
  }, [])

  const handleToggleActive = useCallback(async (plan: AdminPlan) => {
    setActionLoading(plan.id)
    setActionError(null)
    try {
      await toggleActive(plan)
    } catch (err: any) {
      setActionError(err.message)
    } finally {
      setActionLoading(null)
    }
  }, [toggleActive])

  const handleSyncStripe = useCallback(async (plan: AdminPlan) => {
    setActionLoading(plan.id)
    setActionError(null)
    try {
      await syncStripe(plan.id)
    } catch (err: any) {
      setActionError(err.message)
    } finally {
      setActionLoading(null)
    }
  }, [syncStripe])

  const handleShowAudit = useCallback(async (planId: number) => {
    if (showAudit === planId) {
      setShowAudit(null)
      return
    }
    try {
      const logs = await getAuditLog(planId)
      setAuditLog(logs)
      setShowAudit(planId)
    } catch {
      setAuditLog([])
    }
  }, [showAudit, getAuditLog])

  const handleDelete = useCallback(async (plan: AdminPlan) => {
    const confirmed = window.confirm(
      `Supprimer définitivement le plan "${plan.name}" ?\n\nCette action est irréversible.`
    )
    if (!confirmed) return
    
    setActionLoading(plan.id)
    setActionError(null)
    try {
      await deletePlan(plan.id)
    } catch (err: any) {
      setActionError(err.message)
    } finally {
      setActionLoading(null)
    }
  }, [deletePlan])

  const handleEditorClose = useCallback((saved: boolean) => {
    setShowEditor(false)
    setEditingPlan(null)
    if (saved) {
      refetch()
    }
  }, [refetch])

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background gradient - same as AgentsPage */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        {/* Actions Bar */}
        <div className="flex items-center justify-end gap-3 mb-6">
          {circuitStatus && (
            <CircuitStatusBadge state={circuitStatus.state} />
          )}
          {dlqEntries.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300">
              <AlertTriangle className="w-3 h-3" />
              {dlqEntries.length} erreurs DLQ
            </span>
          )}
          <button
            onClick={() => refetch()}
            className="rounded-full p-2.5 border border-emerald-200/70 bg-white/80 text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-900/40 dark:text-emerald-300"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
          >
            <Plus className="w-4 h-4" />
            Nouveau Plan
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Rechercher par nom ou slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-emerald-100/70 bg-white/80 text-zinc-700 placeholder:text-zinc-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder:text-zinc-500"
            />
          </div>
          
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="appearance-none pl-4 pr-10 py-2.5 rounded-full border border-emerald-100/70 bg-white/80 text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-100"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs uniquement</option>
              <option value="inactive">Inactifs uniquement</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500 pointer-events-none" />
          </div>
        </div>

        {/* Error message */}
        {(error || actionError) && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50/70 border border-red-200/70 text-red-700 flex items-center gap-2 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-300">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error || actionError}</span>
          </div>
        )}

        {/* Plans Table */}
        <div className="rounded-3xl border border-emerald-100/70 bg-white/90 shadow-xl shadow-emerald-500/10 overflow-hidden dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-emerald-50/50 border-b border-emerald-100/70 dark:bg-zinc-900/50 dark:border-emerald-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Prix
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Limites
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Abonnés
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-emerald-100/70 dark:divide-zinc-800">
                {loading ? (
                  // Skeleton loading
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-4">
                        <div className="h-4 bg-zinc-800 rounded w-32" />
                        <div className="h-3 bg-zinc-800 rounded w-24 mt-2" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-zinc-800 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-zinc-800 rounded w-28" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-5 bg-zinc-800 rounded-full w-16" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-8 bg-zinc-800 rounded w-24 ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : filteredPlans.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                      {searchTerm || filterStatus !== 'all' 
                        ? 'Aucun plan ne correspond aux filtres'
                        : 'Aucun plan créé. Cliquez sur "Nouveau Plan" pour commencer.'}
                    </td>
                  </tr>
                ) : (
                  filteredPlans.map((plan) => (
                    <React.Fragment key={plan.id}>
                      <tr className="hover:bg-emerald-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                        {/* Plan info */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-zinc-900 dark:text-white">
                                  {plan.name}
                                </span>
                                {plan.is_featured && (
                                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                )}
                              </div>
                              <div className="text-sm text-emerald-600 dark:text-emerald-400 font-mono">
                                {plan.slug}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        {/* Price */}
                        <td className="px-4 py-4">
                          <div className="text-zinc-900 dark:text-white">
                            {plan.price_monthly === 0 ? (
                              <span className="text-emerald-400">Gratuit</span>
                            ) : (
                              <>
                                {currencyFormatter.format(plan.price_monthly)}
                                <span className="text-zinc-500 text-sm">/mois</span>
                              </>
                            )}
                          </div>
                          {plan.price_yearly && plan.price_yearly > 0 && (
                            <div className="text-sm text-zinc-500">
                              {currencyFormatter.format(plan.price_yearly)}/an
                            </div>
                          )}
                        </td>
                        
                        {/* Limits */}
                        <td className="px-4 py-4">
                          <div className="text-sm text-zinc-700 dark:text-zinc-300 space-y-0.5">
                            <div>
                              {plan.max_agents === -1 ? '∞' : plan.max_agents} agents
                            </div>
                            <div className="text-zinc-500">
                              {plan.max_conversations_per_month === -1 ? '∞' : plan.max_conversations_per_month} conv/mois
                            </div>
                          </div>
                        </td>
                        
                        {/* Status */}
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1.5">
                            <StatusBadge active={plan.is_active} hidden={plan.is_hidden} />
                            <StripeBadge synced={!!plan.stripe_product_id} />
                          </div>
                        </td>
                        
                        {/* Active Subscriptions */}
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-medium ${
                            plan.active_subscriptions > 0
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                              : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                          }`}>
                            {plan.active_subscriptions}
                          </span>
                        </td>
                        
                        {/* Actions */}
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(plan)}
                              className="p-2 rounded-full hover:bg-emerald-100 text-zinc-500 hover:text-emerald-600 transition-colors dark:hover:bg-zinc-700 dark:text-zinc-400 dark:hover:text-white"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            
                            <button
                              onClick={() => handleShowAudit(plan.id)}
                              className={`p-2 rounded-full transition-colors ${
                                showAudit === plan.id ? 'bg-emerald-100 text-emerald-600 dark:bg-zinc-700 dark:text-white' : 'text-zinc-500 hover:bg-emerald-100 hover:text-emerald-600 dark:text-zinc-400 dark:hover:bg-zinc-700 dark:hover:text-white'
                              }`}
                              title="Historique"
                            >
                              <History className="w-4 h-4" />
                            </button>
                            
                            {plan.price_monthly > 0 && (
                              <button
                                onClick={() => handleSyncStripe(plan)}
                                disabled={actionLoading === plan.id}
                                className="p-2 rounded-full hover:bg-indigo-100 text-zinc-500 hover:text-indigo-600 transition-colors disabled:opacity-50 dark:hover:bg-indigo-900/50 dark:text-zinc-400 dark:hover:text-indigo-300"
                                title="Synchroniser Stripe"
                              >
                                {actionLoading === plan.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CreditCard className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleToggleActive(plan)}
                              disabled={actionLoading === plan.id}
                              className={`p-2 rounded-full transition-colors disabled:opacity-50 ${
                                plan.is_active 
                                  ? 'hover:bg-red-100 text-zinc-500 hover:text-red-600 dark:hover:bg-red-900/50 dark:text-zinc-400 dark:hover:text-red-300'
                                  : 'hover:bg-emerald-100 text-zinc-500 hover:text-emerald-600 dark:hover:bg-emerald-900/50 dark:text-zinc-400 dark:hover:text-emerald-300'
                              }`}
                              title={plan.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {plan.is_active ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => handleDelete(plan)}
                              disabled={actionLoading === plan.id}
                              className="p-2 rounded-full hover:bg-red-100 text-zinc-500 hover:text-red-600 transition-colors disabled:opacity-50 dark:hover:bg-red-900/50 dark:text-zinc-400 dark:hover:text-red-400"
                              title="Supprimer définitivement"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Audit log row */}
                      {showAudit === plan.id && (
                        <tr>
                          <td colSpan={5} className="px-4 py-4 bg-emerald-50/30 dark:bg-zinc-800/30">
                            <div className="text-sm">
                              <h4 className="font-medium text-zinc-700 dark:text-zinc-300 mb-3 flex items-center gap-2">
                                <History className="w-4 h-4" />
                                Historique des modifications
                              </h4>
                              {auditLog.length === 0 ? (
                                <p className="text-zinc-500">Aucune modification enregistrée</p>
                              ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {auditLog.map((entry) => (
                                    <div key={entry.id} className="flex items-start gap-3 p-2 rounded-xl bg-white/70 border border-emerald-100/50 dark:bg-zinc-900/50 dark:border-zinc-800">
                                      <div className="text-zinc-500 text-xs whitespace-nowrap">
                                        {dateFormatter.format(new Date(entry.created_at))}
                                      </div>
                                      <div className="flex-1">
                                        <span className="text-zinc-700 dark:text-zinc-300">{entry.action}</span>
                                        {entry.changed_by && (
                                          <span className="text-zinc-500 ml-2">par {entry.changed_by}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stats footer */}
        <div className="mt-6 flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-400">
          <div>
            {filteredPlans.length} plan{filteredPlans.length > 1 ? 's' : ''} 
            {filterStatus !== 'all' && ` (filtré${filteredPlans.length > 1 ? 's' : ''})`}
          </div>
          <div className="flex items-center gap-4">
            <span>{plans.filter(p => p.is_active).length} actifs</span>
            <span>{plans.filter(p => !!p.stripe_product_id).length} synchronisés Stripe</span>
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <PlanEditorModal
          plan={editingPlan}
          onClose={handleEditorClose}
        />
      )}
    </div>
  )
}
